<script lang="ts">
  import { onMount } from 'svelte'
  import SanityImage from '#lib/components/SanityImage.svelte'

  type NavItem = {
    id: string
    title: string
    imageUrl: string
  }

  type Props = {
    items: NavItem[]
  }

  let { items }: Props = $props()

  let activeSection = $state('')
  let navElement = $state<HTMLElement | null>(null)
  const buttonRefs: Record<string, HTMLElement> = $state({})

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (!element || !navElement) {
      return
    }
    const navHeight = navElement.offsetHeight
    // Artwork tiles use content-visibility:auto, so the heights of the
    // sections above the target are estimates until they render. Each scroll
    // renders the tiles we pass, correcting the target's offsetTop; converge
    // over a few frames until it stops moving. Instant, not smooth — smooth-
    // scrolling a 60k-px collection would crawl through everything.
    let last = -1
    let tries = 0
    const settle = () => {
      const top = element.offsetTop - navHeight
      window.scrollTo({ top, behavior: 'auto' })
      if (Math.abs(top - last) > 2 && ++tries < 25) {
        last = top
        requestAnimationFrame(settle)
      }
    }
    settle()
  }

  const updateActiveSection = () => {
    const sections = items.map((item) => ({
      id: item.id,
      element: document.getElementById(item.id)
    }))

    const scrollPosition = window.scrollY + 200

    for (let i = sections.length - 1; i >= 0; i--) {
      const section = sections[i]
      if (section?.element && section.element.offsetTop <= scrollPosition) {
        activeSection = section.id
        break
      }
    }
  }

  const currentNav = (): HTMLElement | null => navElement

  const scrollActiveButtonIntoView = () => {
    const activeButton = buttonRefs[activeSection]
    if (activeButton && navElement) {
      // Use requestAnimationFrame to avoid forced reflow
      requestAnimationFrame(() => {
        // Re-read: bind:this clears it if the nav unmounted before this frame.
        const nav = currentNav()
        if (!nav) {
          return
        }
        const navRect = nav.getBoundingClientRect()
        const buttonRect = activeButton.getBoundingClientRect()

        // Check if button is outside the visible area
        if (buttonRect.left < navRect.left || buttonRect.right > navRect.right) {
          activeButton.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          })
        }
      })
    }
  }

  // Watch for activeSection changes and scroll button into view
  $effect(() => {
    if (activeSection !== '') {
      scrollActiveButtonIntoView()
    }
  })

  onMount(() => {
    window.addEventListener('scroll', updateActiveSection, { passive: true })
    updateActiveSection() // Set initial active section

    return () => {
      window.removeEventListener('scroll', updateActiveSection)
    }
  })
</script>

<nav
  bind:this={navElement}
  class="sticky top-16 left-0 z-50 overflow-x-scroll border-t border-b border-black bg-white/50 shadow-sm backdrop-blur-md lg:overflow-x-auto"
>
  <div class="container py-2.5 lg:mx-auto">
    <div class="flex items-center justify-start gap-5 px-5 lg:justify-between">
      {#each items as item (item.id)}
        <button
          bind:this={buttonRefs[item.id]}
          onclick={() => {
            scrollToSection(item.id)
          }}
          data-active={activeSection === item.id}
          data-umami-event="nav-artist-click"
          data-umami-event-artist={item.title}
          class="flex shrink-0 flex-row items-center gap-1 grayscale transition-all duration-200 last:pr-5 data-[active=true]:scale-105 data-[active=true]:grayscale-0"
        >
          <div
            data-active={activeSection === item.id}
            class="aspect-square h-[50px] w-[50px] overflow-hidden rounded-full border-2 border-transparent data-[active=true]:border-black lg:h-[75px] lg:w-[75px]"
          >
            <SanityImage
              src={item.imageUrl}
              alt={item.title}
              className="w-full h-full object-cover transition-all duration-200 hidden lg:block"
              quality={80}
              width={75}
              height={75}
            />
            <SanityImage
              src={item.imageUrl}
              alt={item.title}
              className="w-full h-full object-cover transition-all duration-200 lg:hidden"
              quality={80}
              width={50}
              height={50}
            />
          </div>
          <span
            data-active={activeSection === item.id}
            class="max-w-16 text-left text-xs leading-tight font-medium text-black data-[active=true]:font-bold"
          >
            {item.title}
          </span>
        </button>
      {/each}
    </div>
  </div>
</nav>
